import type { CostPeriod, CostSummaryResponse } from "../api/types";

/**
 * Блок стоимости потреблённой энергии по биржевым ценам Nord Pool.
 *
 * Показывает текущую цену и сколько потрачено за календарные сутки и месяц.
 * Цена — чистая биржевая (day-ahead), без сетевых тарифов, акцизов и НДС;
 * об этом сказано в подписи, чтобы число не приняли за сумму из платёжки.
 */
export function CostSummary({ summary }: { summary: CostSummaryResponse }) {
  const { currency } = summary;
  return (
    <div className="cost">
      <div className="cost-head">
        <span className="cost-title">Стоимость</span>
        <span className="badge">Nord Pool · зона {summary.zone}</span>
        <span className="muted">биржевая цена, без сетевых тарифов и НДС</span>
      </div>

      <div className="cost-figures">
        <div className="cost-figure">
          <div className="label">Текущая цена</div>
          <div className="value">
            {summary.spotPerKWh !== undefined ? (
              <>
                {formatPrice(summary.spotPerKWh, currency)}
                <span className="unit">/кВт·ч</span>
              </>
            ) : (
              <span className="muted">нет данных</span>
            )}
          </div>
        </div>

        <PeriodFigure label="Потрачено сегодня" period={summary.day} currency={currency} />
        <PeriodFigure label="Потрачено за месяц" period={summary.month} currency={currency} />
      </div>

      {noteFor(summary) && <div className="muted cost-note">{noteFor(summary)}</div>}
    </div>
  );
}

/** Плитка периода: сумма крупно, потребление подписью снизу. */
function PeriodFigure({
  label,
  period,
  currency,
}: {
  label: string;
  period: CostPeriod;
  currency: string;
}) {
  return (
    <div className="cost-figure accent">
      <div className="label">{label}</div>
      <div className="value">{formatMoney(period.amount, currency)}</div>
      <div className="cost-sub">{formatKWh(period.kwh)} кВт·ч</div>
    </div>
  );
}

/** Заметное «без тарифа» показываем, только когда его доля ощутима. */
function noteFor(s: CostSummaryResponse): string | null {
  const m = s.month;
  if (m.missing > 0.001 && m.missing > m.kwh * 0.02) {
    return `За месяц ${formatKWh(m.missing)} кВт·ч без тарифа — на эти интервалы цены пока нет в базе.`;
  }
  return null;
}

/** Цена за кВт·ч: до 4 знаков, символ валюты после числа. */
function formatPrice(v: number, currency: string): string {
  const num = new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(v);
  return `${num} ${currencySymbol(currency)}`;
}

/** Сумма: у малых значений больше знаков, чтобы копейки не схлопывались в ноль. */
function formatMoney(v: number, currency: string): string {
  const digits = v !== 0 && Math.abs(v) < 1 ? 4 : 2;
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(v);
}

function formatKWh(v: number): string {
  return new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: v < 1 ? 3 : 1,
    maximumFractionDigits: 3,
  }).format(v);
}

function currencySymbol(currency: string): string {
  const symbols: Record<string, string> = { EUR: "€", SEK: "kr", NOK: "kr", DKK: "kr", PLN: "zł" };
  return symbols[currency] ?? currency;
}
